/** User-facing Italian labels for domain values. Presentation only; no business logic. */
import type { AppliedAccountingRule, PaymentMethodKind } from "@/domain/payment";
import type { HierarchyWarning } from "@/domain/categories/hierarchy";
import type { DuplicateLevel, DuplicateReason } from "@/domain/duplicates";
import type { SyncStatus } from "@/domain/expense";
import type { IncomeType } from "@/domain/income";
import type { CostNature } from "@/domain/model";

/** Italian wording for category-import warnings (stored as structured JSON; older rows may be plain text). */
export function describeCategoryWarning(warning: HierarchyWarning | string): string {
  if (typeof warning === "string") return warning;
  switch (warning.code) {
    case "case-collision-with-group":
      return (
        `La categoria "${warning.label}" (riga ${warning.row}) differisce dal gruppo "${warning.groupLabel}" ` +
        `(riga ${warning.groupRow}) solo per le maiuscole. Excel le considera uguali, quindi come categoria di una spesa ` +
        `si usa sempre "${warning.label}", mai il nome del gruppo.`
      );
    case "empty-group":
      return `Il gruppo "${warning.label}" (riga ${warning.row}) non contiene categorie.`;
    case "whitespace":
      return `La voce "${warning.label}" (riga ${warning.row}) ha spazi all'inizio o alla fine: è stata importata così com'è.`;
  }
}

export const ACCOUNTING_RULE_LABELS: Record<AppliedAccountingRule, string> = {
  same_month: "Stesso mese dell'acquisto",
  next_month: "Mese successivo all'acquisto",
  manual_override: "Mese scelto a mano",
};

export const INCOME_TYPE_LABELS: Record<IncomeType, string> = {
  salary: "Stipendio",
  bonus: "Premio / bonus",
  reimbursement: "Rimborso",
  gift: "Regalo",
  other: "Altro",
};

export const COST_NATURE_LABELS: Record<CostNature, string> = {
  fixed: "Fissa",
  discretionary: "Discrezionale",
};

export const SYNC_STATUS_LABELS: Record<SyncStatus, string> = {
  not_queued: "Nel file Excel",
  pending: "Da sincronizzare",
  exported: "Esportata",
  synced: "Sincronizzata",
  failed: "Errore",
  blocked: "Bloccata",
};

export const DUPLICATE_LEVEL_LABELS: Record<DuplicateLevel, string> = {
  likely: "Molto probabile",
  possible: "Possibile",
};

export const DUPLICATE_REASON_LABELS: Record<DuplicateReason, string> = {
  same_amount: "stesso importo",
  similar_amount: "importo quasi uguale",
  same_date: "stessa data",
  near_date: "data vicina",
  same_merchant: "stesso esercente",
  similar_merchant: "esercente simile",
  merchant_unknown: "esercente non indicato",
  same_category: "stessa categoria",
  same_payment_method: "stesso metodo di pagamento",
  different_payment_method: "metodo di pagamento diverso",
  payment_method_unknown: "metodo di pagamento non indicato",
};

export const PAYMENT_KIND_LABELS: Record<PaymentMethodKind, string> = {
  cash: "Contanti",
  credit_card: "Carta di credito",
  debit_card: "Carta di debito",
  prepaid_card: "Carta prepagata",
  wallet: "Portafoglio digitale",
  bank_transfer: "Bonifico",
  other: "Altro",
};
